import React from 'react';
import moment from 'moment';
import { useTranslation, TFunction, Trans } from 'react-i18next';

// ui
import Text from './shared/Typography';
import CardSplit from './shared/CardSplit';
import Card from './shared/Card';

// style
import { ImmunizationResource } from '../types/resource';
// utilities, services
import { formatDate } from '../utils/format';

type TimelineItem = {
  administerDate: ?Date,
  vaccineName: string,
  abbreviation: string,
  diseaseName: string,
  status: 'received' | 'upcoming' | 'now' | 'missed',
  resource: ImmunizationResource
}

const TimelineCard = (props: { item: TimelineItem, key: number, onPressdivSource: (url: string) => void }) => {
  const { administerDate, abbreviation, vaccineName, diseaseName, status, resource } = props.item;
  const { t } = useTranslation('immunizationTimelineCard');
  const resourceTextExists = resource && resource.full_text && resource.full_text.length > 0;

  let dateText: string;
  let subheader: string;
  let highlightColor: string;
  let bodyContent: any =
    resource && resource.full_text &&
    <Text paragraph className='immunizations__timeline-body'>{resource.full_text}</Text>;

  const vaccineLocal = t('vaccine');
  let vaccineText = vaccineName || abbreviation || vaccineLocal;
  if (!vaccineText.toLowerCase().includes(vaccineLocal)) vaccineText += ` ${vaccineLocal}`;

  let instructionTranslationKey =
    diseaseName ? `instructions.${status}.disease_name` : `instructions.${status}.no_disease_name`;
  let upcomingDate: any;

  switch (status) {
  case 'received':
    highlightColor = 'is-success';
    bodyContent = bodyForResource(resource, t);
    subheader = resource.institution_name &&
      t('instructions.received.subheader', { institution_name: resource.institution_name });
    dateText = administerDate ? formatDate(administerDate) : '';
    break;

  case 'now': {
    highlightColor = 'error';
    const lastDose = resource.completedDoses && resource.completedDoses[resource.completedDoses.length - 1];
    subheader = '';
    if (resource.dosage_interval) {
      subheader = t('instructions.now.subheader_interval', { count: resource.dosage_interval });
    }
    if (lastDose && lastDose.administerDate) {
      subheader = t('instructions.now.subheader_last_dose', { date: formatDate(lastDose.administerDate) });
    }
    break;
  }

  case 'upcoming':
    highlightColor = 'warning';
    if (resource.abbreviation === 'Inf') {
      const currentMonth = moment().month();
      const infStartMonth = 7; // September
      upcomingDate = Math.ceil(infStartMonth - currentMonth);
      instructionTranslationKey += '.inf';
    } else {
      upcomingDate = resource.after_age;
      instructionTranslationKey += '.at_age';
    }
    break;

  case 'missed':
    dateText = administerDate ? moment(administerDate).format('YYYY') : '';
    highlightColor = 'error';
    break;

  default:
    instructionTranslationKey = null;
  }
  if (!upcomingDate) {
    if (dateText) {
      instructionTranslationKey += '.with_date';
    } else if (dateText !== undefined) {
      instructionTranslationKey += '.without_date';
    }
  }
  const header = (
    <Text paragraph>
      {
        instructionTranslationKey && (
          <Trans
            i18nKey={instructionTranslationKey}
            t={t}
            values={{
              vaccine_text: vaccineText,
              disease_name: diseaseName,
              date_text: dateText,
              num: upcomingDate
            }}
            components={{
              colour: <Text span bold color={highlightColor} />
            }}
          />
        )
      }
    </Text>
  );

  if (subheader || resourceTextExists || bodyContent) {
    return (
      <CardSplit
        className='immunizations__timeline-card'
        noShadow
        key={props.key}
        header={header}
      >
        {subheader && <Text paragraph>{subheader}</Text>}
        {resourceTextExists && <Text paragraph>{t('canadian_immunizations_guide_says')}</Text>}
        {bodyContent}
        {resourceTextExists
          && <Text link href={resource.source} className='immunizations__timeline-link'>{t('read_more')}</Text>
        }
      </CardSplit>
    );
  }
  return (
    <Card className='immunizations__timeline-card' noShadow key={props.key}>
      {header}
    </Card>
  );
};

const bodyForResource = (resource: ImmunizationResource, t: TFunction) => {
  if (!resource) return null;
  const { name, description, din, dosage, drugName, route, series, targetDisease } = resource;
  const showFields = [{
    label: t('labels.full_name'),
    value: name && name.text
  }, {
    label: t('labels.abbreviation'),
    value: name && name.coding && name.coding.code
  }, {
    label: t('labels.description'),
    value: description
  }, {
    label: t('labels.drug_name'),
    value: drugName
  }, {
    label: t('labels.din'),
    value: din
  }, {
    label: t('labels.target_diseases'),
    value: targetDisease && targetDisease.text
  }, {
    label: t('labels.route_of_administration'),
    value: route
  }, {
    label: t('labels.series'),
    value: series
  }, {
    label: t('labels.dosage'),
    value: dosage
  }, {
    label: t('labels.practitioner'),
    value: resource.practitioner
  }];

  if (showFields.length === 0) return null;

  return (
    <div>
      {showFields.map((field, i) => <ResourceBodyField {...field} key={i} />)}
    </div>
  );
};

const ResourceBodyField = (props: { label: string, value: string, key: number }) => {
  if (!props.label || !props.value || !props.value.length || !props.label.length) return null;
  return (
    <div key={props.key}>
      <Text bold>{props.label}: </Text>
      <Text paragraph>{props.value}</Text>
    </div>
  );
};

export default TimelineCard;


// WEBPACK FOOTER //
// ./src/components/immunization-timeline-card.js